import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import AssetType from '../../components/Inputs/AssetType';
import Input from '../../common/Form/Input';
import Loader from '../../common/Loader';
import { actualValueForDropdown } from '../../utils/common';
import PavDynamicAssetField from './PavDynamicAssetField';
import {
    findAssetTypeOption,
    getSavedAssetFields,
    getSavedAssetOwner,
    getSavedAssetTypeId,
    getSavedAssetTypeLabel,
    normalizeOcrKey,
    serializePavAssetFieldValue,
} from './pavAssetUtils';

const INPUT_STYLE = { height: '2.5rem' };
const INPUT_CLASS = 'block w-full border-b-2 border-l-0 border-r-0 border-t-0 border-scogoddd bg-white px-0 text-font11 font-normal text-scogo21 placeholder:text-font10 placeholder:font-normal placeholder:text-inputfieldtext focus:border-scogoprimary focus:ring-0';
const LABEL_CLASS = '!text-font10 !font-medium !text-slate-600';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getAssetTypeFields = (asset) => (
    asset?.asset_type?.additional_fields
    || asset?.asset_type?.fields
    || asset?.assetType?.additional_fields
    || asset?.assetType?.fields
    || asset?.asset?.asset_type?.additional_fields
    || asset?.asset?.asset_type?.fields
    || asset?.asset?.assetType?.additional_fields
    || asset?.asset?.assetType?.fields
    || []
);

const mergeFieldDefinitions = (...fieldLists) => {
    const byKey = new Map();
    fieldLists.flat().forEach((field) => {
        const key = field?.key || field?.name || field?.label;
        const normalizedKey = normalizeOcrKey(key);
        if (!normalizedKey || ['asset_type', 'asset_type_id'].includes(normalizedKey)) return;
        const current = byKey.get(normalizedKey);
        byKey.set(normalizedKey, {
            ...current,
            ...field,
            key,
            label: field?.label || current?.label || key,
        });
    });
    return Array.from(byKey.values());
};

export default function PavSavedAssetEditForm({ asset, assetTypes, orgId, isCompact, isSaving, isDisabled = false, onSubmit, onCancel }) {
    const savedFields = useMemo(() => getSavedAssetFields(asset), [asset]);
    const savedOwner = useMemo(() => getSavedAssetOwner(asset), [asset]);
    const savedTypeId = getSavedAssetTypeId(asset);
    const savedTypeLabel = getSavedAssetTypeLabel(asset);
    const savedFieldDefinitions = useMemo(() => mergeFieldDefinitions(
        getAssetTypeFields(asset),
        savedFields
    ), [asset, savedFields]);
    const catalogueType = useMemo(() => (
        findAssetTypeOption(assetTypes?.catalogue, savedTypeId)
        || findAssetTypeOption(assetTypes?.catalogue, savedTypeLabel)
    ), [assetTypes?.catalogue, savedTypeId, savedTypeLabel]);
    const fallbackType = useMemo(() => ({
        id: savedTypeId || savedTypeLabel,
        key: savedTypeId || savedTypeLabel,
        value: savedTypeId || savedTypeLabel,
        label: savedTypeLabel,
        fields: savedFieldDefinitions,
        assetType: {
            id: savedTypeId,
            title: savedTypeLabel,
            additional_fields: savedFieldDefinitions,
        },
    }), [savedTypeId, savedTypeLabel, savedFieldDefinitions]);
    const initialType = catalogueType || fallbackType;
    const defaultValues = useMemo(() => ({
        asset_type_id: initialType,
        ...Object.fromEntries(savedFields.map((field) => [field.key, field.value ?? ''])),
        owner_name: savedOwner.name,
        contact_email: savedOwner.email,
    }), [initialType, savedFields, savedOwner.email, savedOwner.name]);
    const methods = useForm({ mode: 'onChange', defaultValues });
    const selectedType = methods.watch('asset_type_id');

    // Replace the local fallback with the complete catalogue option when the
    // full asset-type request finishes, without making the form appear dirty.
    useEffect(() => {
        if (!catalogueType) return;
        const currentType = methods.getValues('asset_type_id');
        if (String(actualValueForDropdown(currentType)) !== String(actualValueForDropdown(catalogueType))) return;
        if (currentType !== catalogueType) {
            methods.setValue('asset_type_id', catalogueType, { shouldDirty: false, shouldValidate: true });
        }
    }, [catalogueType, methods]);

    const selectedTypeId = actualValueForDropdown(selectedType);
    const isOriginalType = String(selectedTypeId) === String(savedTypeId || savedTypeLabel);
    const editableFields = useMemo(() => mergeFieldDefinitions(
        selectedType?.fields || selectedType?.assetType?.additional_fields || [],
        isOriginalType ? savedFieldDefinitions : []
    ), [selectedType, isOriginalType, savedFieldDefinitions]);

    const submit = (values) => {
        const assetTypeId = actualValueForDropdown(values.asset_type_id);
        const additionalFields = editableFields.map((field) => ({
            key: field.key,
            value: serializePavAssetFieldValue(field, values[field.key]),
        }));
        const serialNumber = additionalFields.find((field) => normalizeOcrKey(field.key) === 'serial_number')?.value || '';
        const ownerName = String(values.owner_name || '').trim();
        const ownerEmail = String(values.contact_email || '').trim();
        const owner = ownerName || ownerEmail ? {
            ...(ownerName ? { name: ownerName } : {}),
            ...(ownerEmail ? { email: ownerEmail } : {}),
            ...(savedOwner.mobile ? { mobile: savedOwner.mobile } : {}),
        } : null;
        const hadSavedOwner = Boolean(savedOwner.name || savedOwner.email || savedOwner.mobile);
        const selectedAssetType = values.asset_type_id?.assetType || {};
        const assetTypeLabel = values.asset_type_id?.label || savedTypeLabel;
        const fieldPatch = Object.fromEntries(additionalFields.map(({ key, value }) => [key, value]));

        onSubmit?.({
            payload: {
                asset_type_id: assetTypeId,
                serial_number: serialNumber,
                additional_fields: additionalFields,
                ...((owner || hadSavedOwner) ? { owner } : {}),
            },
            assetPatch: {
                ...fieldPatch,
                asset_type_id: assetTypeId,
                asset_type_name: assetTypeLabel,
                asset_type: {
                    ...selectedAssetType,
                    id: assetTypeId,
                    title: assetTypeLabel,
                    additional_fields: editableFields,
                },
                serial_number: serialNumber,
                additional_fields: additionalFields,
                owner,
                asset_owner: owner,
                owner_name: ownerName,
                contact_email: ownerEmail,
            },
        });
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(submit)} className='mt-2 rounded-md border border-blue-200 bg-blue-50/40 p-2' onKeyDown={(event) => {
                if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA') event.preventDefault();
            }}>
                <div className={`grid gap-x-2 ${isCompact ? 'grid-cols-1' : 'grid-cols-2'}`}>
                    <AssetType
                        label='Asset Type'
                        name='asset_type_id'
                        org_id={orgId}
                        required
                        clearOnUnmount={false}
                        reuseLoadedOptions
                        labelClassName={LABEL_CLASS}
                    />
                    {editableFields.map((field) => (
                        <PavDynamicAssetField
                            key={field.key}
                            field={field}
                            inputStyle={INPUT_STYLE}
                            inputClassName={INPUT_CLASS}
                            labelClassName={LABEL_CLASS}
                            disableResponsiveFontSize
                            isPavMilestoneAsset
                        />
                    ))}
                    <Input label='Owner Name' name='owner_name' placeholder='Enter owner name (optional)' inputStyle={INPUT_STYLE} inputClassName={INPUT_CLASS} labelClassName={LABEL_CLASS} disableResponsiveFontSize />
                    <Input label='Owner Email' name='contact_email' type='email' placeholder='Enter owner email (optional)' pattern={EMAIL_PATTERN} inputStyle={INPUT_STYLE} inputClassName={INPUT_CLASS} labelClassName={LABEL_CLASS} disableResponsiveFontSize />
                </div>
                <div className='flex justify-end gap-2 border-t border-blue-100 pt-2'>
                    <button type='button' disabled={isSaving || isDisabled} onClick={onCancel} className={`h-8 rounded-lg border border-slate-300 bg-white px-3 text-font10 font-semibold text-slate-600 transition-colors ${isSaving || isDisabled ? 'cursor-not-allowed opacity-50' : 'hover:border-slate-400 hover:text-scogo21'}`}>
                        Cancel
                    </button>
                    <button type='submit' disabled={isSaving || isDisabled} className={`flex h-8 min-w-[5.5rem] items-center justify-center gap-1.5 rounded-lg bg-scogoprimary px-3 text-font10 font-semibold text-white shadow-sm transition-colors ${isSaving || isDisabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-slate-800'}`}>
                        {isSaving ? <Loader size='12' thickness='2' color='#ffffff' /> : <span className='material-icons text-font13'>save</span>}
                        {isSaving ? 'Saving...' : 'Save changes'}
                    </button>
                </div>
            </form>
        </FormProvider>
    );
}
